import React, { useEffect, useState } from "react";
import { getSugerencias } from "../services/API";
import Layout from "./Layout";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Typography,
    Chip,
    IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/sugerencias.css";
import alertify from "alertifyjs";
import "alertifyjs/build/css/alertify.css";
import "alertifyjs/build/css/themes/default.css";
import 'bootstrap/dist/css/bootstrap.min.css';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
    "&:hover": {
        backgroundColor: theme.palette.action.selected,
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
    padding: "12px 16px",
}));

const MODALIDAD_MAP = {
    1: "Respective Ad Group",
    2: "All Ad Groups",
    3: "Select Ad Groups",
    4: "Respective Campaign",
    5: "All Campaigns",
    6: "Select Campaigns",
    7: "Respective Portfolio",
    8: "All Portfolios",
    9: "Select Portfolios",
    10: "Respective ASIN",
    11: "All ASINs",
    12: "Select ASINs",
};

function Sugerencias() {
    const [sugerencias, setSugerencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAccountLabel, setSelectedAccountLabel] = useState("");

    useEffect(() => {
        const storedLabel = localStorage.getItem("selectedAccountLabel");
        if (storedLabel) {
            setSelectedAccountLabel(storedLabel);
        } else {
            setError("No account selected.");
            setLoading(false);
            return;
        }

        const fetchSugerenciasData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await getSugerencias();
                console.log("getSugerencias response:", response);

                if (response && Array.isArray(response.data)) {
                    const filteredSugerencias = response.data.filter(
                        (sug) => sug.ads_account === storedLabel
                    );
                    setSugerencias(filteredSugerencias);
                } else {
                    console.error("Unexpected suggestions data format:", response);
                    setSugerencias([]);
                    setError("Unexpected data format received from the server.");
                }
            } catch (err) {
                console.error("Error fetching suggestions:", err);
                setError(
                    "Error loading suggestions. Please try again later."
                );
                setSugerencias([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSugerenciasData();
    }, []);

    const handleDelete = (sugerenciaId) => {
        alertify.confirm(
            "Confirm Deletion",
            "Are you sure you want to delete this suggestion?",
            () => {
                setSugerencias(sugerencias.filter(sug => sug.id !== sugerenciaId));
                alertify.success(`Suggestion ${sugerenciaId} deleted.`);
            },
            () => {
                alertify.error("Suggestion deletion canceled.");
            }
        ).set("labels", { ok: "Yes, delete", cancel: "Cancel" });
    };

    return (
        <Layout>
            <div className="app-container">
                <Box className="container mt-4" sx={{ padding: 3 }}>
                    <Typography variant="h4" component="h2" gutterBottom sx={{ color: "primary.dark", mb: 4 }}>
                        Suggestions for account: {selectedAccountLabel}
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                            <CircularProgress />
                            <Typography variant="h6" sx={{ ml: 2 }}>Loading suggestions...</Typography>
                        </Box>
                    ) : error ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px", flexDirection: "column" }}>
                            <Typography variant="h6" color="error" gutterBottom>{error}</Typography>
                            <Typography variant="body1" color="text.secondary">
                                There was a problem loading the data. Check your connection and try again later.
                            </Typography>
                        </Box>
                    ) : sugerencias.length > 0 ? (
                        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: "8px", overflow: "hidden" }}>
                            <Table sx={{ minWidth: 750 }} aria-label="suggestions table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>#</StyledTableCell>
                                        <StyledTableCell align="left">User</StyledTableCell>
                                        <StyledTableCell align="left">Ad Account</StyledTableCell>
                                        <StyledTableCell align="left">Report Date</StyledTableCell>
                                        <StyledTableCell align="left">CST</StyledTableCell>
                                        <StyledTableCell align="left">Mode</StyledTableCell>
                                        <StyledTableCell align="left">Recommendation</StyledTableCell>
                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sugerencias.map((sug, index) => {
                                        const modalidadText = MODALIDAD_MAP[sug.modalidad] || "Unknown";
                                        return (
                                            <StyledTableRow key={sug.id || index}>
                                                <TableCell component="th" scope="row">
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell align="left">{sug.usuario}</TableCell>
                                                <TableCell align="left">{sug.ads_account}</TableCell>
                                                <TableCell align="left">{sug.fecha}</TableCell>
                                                <TableCell align="left">{sug.CST}</TableCell>
                                                <TableCell align="left">
                                                    <Chip label={modalidadText} color="primary" size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell align="left">Negativize</TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        aria-label="delete"
                                                        color="error"
                                                        onClick={() => handleDelete(sug.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px", flexDirection: "column", border: "2px dashed #ccc", borderRadius: "8px", p: 3 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No suggestions for this account
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                It looks like there are no recommendations for the selected account yet.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </div>
        </Layout>
    );
}

export default Sugerencias;
